import { Menu, Popover, Tooltip } from '@mantine/core';
import c from 'classnames';
import { capitalize } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Field, Form } from 'react-final-form';
import ReactModal from 'react-modal';
import { v4 as uuid } from 'uuid';
import ProductImage from '../../../common/ProductImage';
import Button from '../../../components/Button';
import ContactSupportLink from '../../../components/ContactSupportLink';
import Error from '../../../components/Error';
import { StyledForm } from '../../../components/Form';
import Modal, { MODAL_STYLES } from '../../../components/Modal';
import { Pill } from '../../../components/Pill';
import IconExternalLink from '../../../components/icons/IconExternalLink';
import IconLink from '../../../components/icons/IconLink';
import IconPayPal from '../../../components/icons/IconPayPal';
import IconStripe from '../../../components/icons/IconStripe';
import { CurrentPlanContext } from '../../../hooks/CurrenPlanContext';
import { CurrentCompanyContext } from '../../../hooks/CurrentCompanyContext';
import { useUpgradeModal } from '../../../hooks/useUpgradeModal';
import { CustomProduct, PricingPage, Product, StripeMode } from '../../../types/pricingPage';
import { formatFrequency, formatPrice, formatPriceData } from '../../../utils/currency';
import { useCompanySettings } from '../../../utils/hooks';
import { isPaidPlan } from '../../../utils/plans';
import {
    getPricingName,
    getStripeMode,
    isBetaBillingScheme,
    isStripePrice,
    isSupported,
} from '../../../utils/products';
import VALIDATORS from '../../../utils/validators';
import PageStatus from '../../../components/PageStatus';

const reorder = (list: Product[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

interface Props {
    page: PricingPage;
    allProducts: Product[];
    selectedProducts: Product[];
    onSelectProduct: (product: Product) => void;
    onRemoveProduct: (product: Product) => void;
    onReloadProducts: () => void;
    onAddProduct: (product: CustomProduct) => void;
    onDeleteProduct: (product: CustomProduct) => void;
    onChangeSelectedProducts: (selectedProducts: Product[]) => void;
    onChangeMode: (mode: PricingPage['status']) => void;
}

export default function ProductChooser({
    page,
    allProducts: initialProducts,
    selectedProducts,
    onSelectProduct,
    onRemoveProduct,
    onReloadProducts,
    onAddProduct,
    onDeleteProduct,
    onChangeSelectedProducts,
    onChangeMode,
}: Props) {
    const { status } = page;
    const company = useContext(CurrentCompanyContext);
    const companySettings = useCompanySettings();
    const currentPlan = useContext(CurrentPlanContext);
    const [UpgradeModal, showUpgradePopup] = useUpgradeModal();
    const shouldShowUpgradePopup = currentPlan && !isPaidPlan(currentPlan);
    const [search, setSearch] = useState('');
    const [allProducts, setAllProducts] = useState(initialProducts);
    const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };
    const paypalEnabled =
        companySettings?.integrations?.paypal?.testClientId || companySettings?.integrations.paypal?.liveClientId;
    useEffect(() => {
        setAllProducts(initialProducts);
    }, [initialProducts]);

    const groupedProducts = useMemo(
        () =>
            Object.entries(
                allProducts.reduce<{ [key: string]: Product[] }>((acc, product) => {
                    if (isStripePrice(product)) {
                        (acc[product.product.name] = acc[product.product.name] || []).push(product);
                    } else {
                        (acc['Custom'] = acc['Custom'] || []).push(product);
                    }
                    return acc;
                }, {})
            ).map(([group, products]) => ({ group, products: products.sort() })),
        [allProducts]
    );

    const handleClickProduct = (product: Product) => {
        if (!!selectedProducts.find((p) => p.id === product.id)) {
            onRemoveProduct(product);
        } else {
            onSelectProduct(product);
        }
    };

    const handleChangePaypalPlanId = (product: Product, planId: string) => {
        const updatedProducts = selectedProducts.map((p) => {
            return p.id === product.id ? { ...p, paypalPlanId: planId } : p;
        });
        setAllProducts(updatedProducts);
        onChangeSelectedProducts(updatedProducts);
    };

    const handleDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const reorderedProducts = reorder(selectedProducts, result.source.index, result.destination.index);

        onChangeSelectedProducts(reorderedProducts);
    };

    const handleSwitchToLive = () => {
        onChangeMode('ACTIVE');
    };
    const isStripeConnected = !!company?.stripe?.stripeId;
    const stripeMode = getStripeMode(status);

    return (
        <div className="grid grid-cols-1 gap-2 pt-8">
            {selectedProducts.length > 0 && (
                <div className="w-full relative">
                    <div className="bg-gray-500 text-white text-sm absolute top-0 left-0 rounded-tl-lg rounded-br-lg px-2">
                        Chosen Products
                    </div>
                    <div className="text-sm absolute top-0 right-0">
                        <SortProductsButton
                            selectedProducts={selectedProducts}
                            onChangeSelectedProducts={onChangeSelectedProducts}
                        />
                    </div>
                    <StyledForm className="px-8 pb-4 overflow-x-scroll">
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="droppable" direction="horizontal">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="flex w-full">
                                        {selectedProducts.map((product, index) => (
                                            <Draggable key={product.id} draggableId={product.id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={provided.draggableProps.style}
                                                        className="p-4 bg-white rounded-lg border shadow mx-2 relative cursor-grab flex flex-col"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-4 h-4 absolute right-1 top-1 rounded-full border bg-gray-900 text-white cursor-pointer"
                                                            onClick={() => onRemoveProduct(product)}
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M6 18L18 6M6 6l12 12"
                                                            />
                                                        </svg>

                                                        <SelectedProduct
                                                            product={product}
                                                            showUpgradeModal={
                                                                shouldShowUpgradePopup ? showUpgradePopup : undefined
                                                            }
                                                            onChangePaypalPlanId={
                                                                paypalEnabled
                                                                    ? (planId) => {
                                                                          handleChangePaypalPlanId(product, planId);
                                                                      }
                                                                    : undefined
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </StyledForm>
                </div>
            )}
            {allProducts?.length !== 0 && (
                <div className="font-bold text-center max-w-4xl mx-auto mb-4">
                    <div>Select Products</div>
                    {isStripeConnected && (
                        <div className="text-sm font-normal">
                            {allProducts
                                ? `We have identified the following products/prices in Stripe ${getStripeMode(
                                      status
                                  )} mode: `
                                : 'Contacting Stripe... '}
                            {allProducts && (
                                <button onClick={onReloadProducts} className="underline">
                                    <img
                                        src="/images/refresh.png"
                                        width="20"
                                        className={c('inline cursor-pointer', { 'animate-spin': !allProducts })}
                                        alt="Reload products from Stripe"
                                    />
                                    Refresh
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 auto-rows-max">
                {groupedProducts && groupedProducts.length > 0 && (
                    <div className="w-full">
                        <StyledForm className="px-8 pb-4">
                            {isStripeConnected && (
                                <div className="flex items-center">
                                    Stripe Mode:{' '}
                                    <span className="ml-2">
                                        <PageStatus
                                            page={page}
                                            treatDraftAsTest
                                            liveWarning={false}
                                            onChangeStatus={onChangeMode}
                                        />
                                    </span>
                                    {allProducts.length > 0 && (
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={handleChangeSearch}
                                            className="ml-auto input border border-gray-400 focus:outline-none focus:bg-white focus:border-gray-500"
                                            placeholder="Search products..."
                                        />
                                    )}
                                </div>
                            )}
                            {groupedProducts.map(({ group, products }) => {
                                const filteredProducts = products.filter((product) =>
                                    search
                                        ? isStripePrice(product) &&
                                          product.product.name.toLowerCase().includes(search.toLowerCase())
                                        : true
                                );
                                if (filteredProducts.length === 0) {
                                    return null;
                                }
                                return (
                                    <React.Fragment key={group}>
                                        <h3>{group}</h3>
                                        {filteredProducts.map((product) => (
                                            <ProductChoice
                                                product={product}
                                                onClick={handleClickProduct}
                                                onDelete={onDeleteProduct}
                                                selected={!!selectedProducts.find((p) => p?.id === product?.id)}
                                                key={product.id}
                                                mode={status === 'ACTIVE' ? StripeMode.LIVE : StripeMode.TEST}
                                            />
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </StyledForm>
                    </div>
                )}
                <AddCustomProductButton
                    onAddProduct={onAddProduct}
                    isStripeConnected={isStripeConnected}
                    hasProductsSelected={!!selectedProducts?.length}
                />
            </div>
            {!isStripeConnected && (
                <div className="text-center flex flex-col my-4">
                    <div>
                        <Pill colour="YELLOW" title="Stripe not connected" />
                    </div>
                    See your real products by clicking "Connect Stripe" above.
                </div>
            )}
            {isStripeConnected && allProducts && allProducts.length === 0 && (
                <div className="text-center font-bold flex-col">
                    <div>No products found in your Stripe account.</div>
                    <div>
                        You are using Stripe {capitalize(stripeMode)} mode.
                        {stripeMode === StripeMode.TEST && (
                            <button onClick={handleSwitchToLive} className="underline ml-2">
                                Switch to Stripe Live mode?
                            </button>
                        )}
                    </div>
                    <button onClick={onReloadProducts} className="underline py-4">
                        <img
                            src="/images/refresh.png"
                            width="20"
                            className={c('inline cursor-pointer', { 'animate-spin': !allProducts })}
                            alt="Reload products from Stripe"
                            onClick={onReloadProducts}
                        />
                        Refresh
                    </button>
                </div>
            )}
            <UpgradeModal />
        </div>
    );
}

interface ProductChoiceProps {
    product: Product;
    onClick: (product: Product) => void;
    onDelete: (product: CustomProduct) => void;
    selected: boolean;
    mode: StripeMode;
}
function ProductChoice({ product, onClick, selected, mode, onDelete }: ProductChoiceProps) {
    const valid = isSupported(product);
    const isBeta = isBetaBillingScheme(product);

    const handleClick = () => (valid ? onClick(product) : undefined);
    const handleDelete = (product: CustomProduct) => {
        if (window.confirm(`Are you sure you want to delete ${product.name}? This is NOT a Stripe product.`)) {
            onDelete(product);
        }
    };

    return (
        <div
            key={product.id}
            onClick={handleClick}
            className={c(
                'relative h-1.5 flex py-8 px-4 mb-4 text-gray-700 border rounded-lg w-full shadow overflow-hidden',
                {
                    'cursor-pointer hover:bg-gray-400': valid,
                    'bg-white': !selected,
                    'bg-gray-400': selected,
                }
            )}
        >
            {!valid && isStripePrice(product) && (
                <div
                    className="absolute inline-flex items-center justify-center inset-0 text-white bg-gray-700 bg-opacity-80 transition-opacity p-4"
                    aria-hidden="true"
                >
                    <span>
                        For {getPricingName(product)} products, please <ContactSupportLink />.
                    </span>
                </div>
            )}
            <div className="flex-auto self-center">
                <input
                    type="checkbox"
                    checked={selected}
                    className="h-4 w-4 mr-2 inline-block align-middle"
                    onClick={handleClick}
                />
                <ProductImage product={product} fillIfMissing={false} className="w-6 mr-2 inline" />
                {isStripePrice(product) ? product.product.name : product.name}
                {isStripePrice(product) && (
                    <a
                        href={`https://dashboard.stripe.com/${mode === StripeMode.TEST ? 'test' : ''}/prices/${
                            product.id
                        }`}
                        className="ml-2 text-blue-600 text-sm hover:text-blue-300"
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <IconStripe className="w-8 h-8 ml-1 inline fill-current" />
                        {mode === StripeMode.TEST ? ' (test)' : ' (live)'}
                        <IconExternalLink className="w-3 h-3 ml-1 inline fill-current" />
                    </a>
                )}
                {isStripePrice(product) && Object.keys(product.currency_options || {}).length >= 2 && (
                    <Tooltip
                        position="top"
                        withArrow
                        label={<CurrencyOptions product={product} />}
                        multiline
                        width={200}
                    >
                        <span className="ml-2">
                            <Pill colour="PURPLE" title="Multi-Currency" small />
                        </span>
                    </Tooltip>
                )}
                {isStripePrice(product) ? (
                    <>
                        {!product.product.active && (
                            <span className="ml-2">
                                <Pill colour="YELLOW" title="Archived" small />
                            </span>
                        )}
                    </>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4 absolute right-1 top-1 rounded-full border bg-gray-900 text-white cursor-pointer"
                        onClick={() => handleDelete(product)}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                )}
            </div>
            <div className="place-self-end self-center">
                {formatPrice(product)} {valid ? formatFrequency(product) : null}
                {isBeta && (
                    <span className="ml-2">
                        <Pill colour="PURPLE" title="BETA" small />
                    </span>
                )}
            </div>
        </div>
    );
}

function SelectedProduct(props: {
    product: Product;
    onChangePaypalPlanId?: (planId: string) => void;
    showUpgradeModal?: () => void;
}) {
    const { product, onChangePaypalPlanId, showUpgradeModal } = props;
    const [showPaypal, setShowPaypal] = useState(!!product.paypalPlanId);
    const [paypalPlanId, setPaypalPlanId] = useState(product.paypalPlanId || '');
    const handleChangePaypalPlanId = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPaypalPlanId(e.target.value);
    };
    const handleSavePaypalPlanId = () => {
        onChangePaypalPlanId?.(paypalPlanId);
        setShowPaypal(false);
    };
    const handleShowPaypalPlan = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (showUpgradeModal) {
            showUpgradeModal();
        } else {
            setShowPaypal(!showPaypal);
        }
    };

    return (
        <>
            {isStripePrice(product) ? product.product.name : product.name}
            {onChangePaypalPlanId && (
                <Popover opened={showPaypal} onChange={setShowPaypal} withArrow trapFocus>
                    <Popover.Target>
                        <button
                            type="button"
                            onClick={handleShowPaypalPlan}
                            className="ml-2 text-blue-600 text-sm hover:text-blue-300 hover:pointer"
                        >
                            <IconPayPal className="h-3 inline" />
                            <IconLink className="w-3 h-3 ml-1 inline fill-current" />
                        </button>
                    </Popover.Target>

                    <Popover.Dropdown>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                value={paypalPlanId}
                                onChange={handleChangePaypalPlanId}
                                className="input"
                                placeholder="PayPal Plan ID"
                            />
                            <Button onClick={handleSavePaypalPlanId}>Ok</Button>
                        </div>
                    </Popover.Dropdown>
                </Popover>
            )}
        </>
    );
}

function CurrencyOptions(props: { product: Product }) {
    const { product } = props;
    if (!isStripePrice(product) || !product.currency_options) {
        return null;
    }
    return (
        <ul>
            {Object.keys(product.currency_options).map((currency) => (
                <li>
                    {formatPriceData({
                        billing_scheme: product.billing_scheme,
                        currency,
                        unit_amount: product.currency_options?.[currency].unit_amount || 0,
                        interval: product.recurring?.interval,
                    })}
                    {formatFrequency(product)}
                </li>
            ))}
        </ul>
    );
}

interface CustomProductFormData {
    id: string;
    name: string;
    price: string;
    callToAction: boolean;
    callToActionLabel?: string;
    callToActionUrl?: string;
}

function AddCustomProductButton(props: {
    onAddProduct: (product: CustomProduct) => void;
    isStripeConnected: boolean;
    hasProductsSelected: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const handleClick = () => {
        setIsOpen(true);
    };
    const handleCloseModal = () => {
        setIsOpen(false);
    };
    const handleSave = ({ name, callToActionLabel, callToActionUrl, price }: CustomProductFormData) => {
        const id = uuid();
        props.onAddProduct({
            id,
            name,
            callToActionLabel,
            callToActionUrl,
            price: { id, customLabel: price },
        });
        handleCloseModal();
    };

    return (
        <>
            <div className="flex mb-4">
                <Button onClick={handleClick} kind="tertiary" className="m-auto">
                    {!props.isStripeConnected
                        ? `Create ${props.hasProductsSelected ? 'another' : props.isStripeConnected ? 'a' : 'an'} ${
                              !props.isStripeConnected ? 'Example' : ''
                          } Pricing Plan`
                        : 'Create Custom Product'}
                </Button>
            </div>
            <Form<CustomProductFormData>
                onSubmit={handleSave}
                initialValues={{
                    id: uuid(),
                    name: 'Pro Plan',
                    price: '$99/mo',
                    callToActionLabel: 'Contact Us',
                    callToActionUrl: 'https://www.pricewell.com',
                    callToAction: true,
                }}
            >
                {({ values, valid, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <ReactModal isOpen={isOpen} style={MODAL_STYLES}>
                            <Modal
                                type="INFO"
                                title="Create a Custom Plan"
                                width="HALF"
                                body={
                                    <div>
                                        <p className="font-bold py-2">
                                            A custom plan is not linked to your Stripe account but can be used to
                                            indicate "Custom" or "Enterprise" pricing. This allows your customers to
                                            register or checkout without entering credit card details.
                                        </p>
                                        <div className="text-left">
                                            <label className="block">
                                                Name
                                                <Field name="name" type="text" validate={VALIDATORS.required}>
                                                    {(props) => (
                                                        <>
                                                            <input
                                                                {...props.input}
                                                                className="input w-full"
                                                                placeholder="Enterprise"
                                                            />
                                                            {props.meta.error && props.meta.touched && (
                                                                <span className="ml-2">
                                                                    <Error message={props.meta.error} />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                            </label>
                                            <label className="block pt-2">
                                                Price
                                                <Field name="price" type="text" validate={VALIDATORS.required}>
                                                    {(props) => (
                                                        <>
                                                            <input
                                                                {...props.input}
                                                                className="input w-full"
                                                                placeholder="$100/mo"
                                                            />
                                                            {props.meta.error && props.meta.touched && (
                                                                <span className="ml-2">
                                                                    <Error message={props.meta.error} />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                            </label>
                                            <div>
                                                <label className="inline-flex items-center pt-2">
                                                    Include call to action button?
                                                    <Field name="callToAction" type="checkbox">
                                                        {(props) => (
                                                            <input
                                                                {...props.input}
                                                                className="checkbox ml-2"
                                                                placeholder="Call Us"
                                                            />
                                                        )}
                                                    </Field>
                                                </label>
                                            </div>
                                            {values.callToAction && (
                                                <>
                                                    <label className="block pt-2">
                                                        Button text
                                                        <Field name="callToActionLabel" type="text">
                                                            {(props) => (
                                                                <input
                                                                    {...props.input}
                                                                    className="input w-full"
                                                                    placeholder="Call Us"
                                                                />
                                                            )}
                                                        </Field>
                                                    </label>
                                                    <label className="block pt-2">
                                                        Button link
                                                        <Field
                                                            name="callToActionUrl"
                                                            type="url"
                                                            validate={VALIDATORS.validUrl}
                                                        >
                                                            {(props) => (
                                                                <>
                                                                    <input
                                                                        {...props.input}
                                                                        className="input w-full"
                                                                        placeholder="https://mysite.com/register"
                                                                    />
                                                                    {props.meta.error && props.meta.touched && (
                                                                        <span className="ml-2">
                                                                            <Error message={props.meta.error} />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Field>
                                                    </label>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                }
                                confirmButtonText="Save"
                                onConfirm={handleSubmit}
                                onCancel={handleCloseModal}
                                confirmDisabled={!valid}
                            />
                        </ReactModal>
                    </form>
                )}
            </Form>
        </>
    );
}

function SortProductsButton(props: {
    selectedProducts: Product[];
    onChangeSelectedProducts: (products: Product[]) => void;
}) {
    const handleSort = (sort: 'HIGH_LOW' | 'LOW_HIGH') => () =>
        props.onChangeSelectedProducts(
            props.selectedProducts.sort((a, b) => {
                if (!isStripePrice(a)) {
                    return 1;
                } else if (!isStripePrice(b)) {
                    return -1;
                } else {
                    return sort === 'HIGH_LOW' ? b.unit_amount - a.unit_amount : a.unit_amount - b.unit_amount;
                }
            })
        );
    return (
        <Menu withArrow>
            <Menu.Target>
                <button
                    className="bg-white text-black border-gray-300 active:bg-grey-600 text-sm rounded shadow hover:shadow-md m-1 px-2"
                    type="button"
                >
                    Sort
                </button>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Item onClick={handleSort('HIGH_LOW')}> Price: High to Low</Menu.Item>
                <Menu.Item onClick={handleSort('LOW_HIGH')}> Price: Low to High</Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}
